import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import api from "../services/api";
import PageSkeleton from "../components/ui/PageSkeleton";
import Input from "../components/ui/Input";
import Button from "../components/ui/Button";
import {
  Person,
  Email,
  Phone,
  Business,
  Work,
  AttachMoney,
  CalendarToday,
  ArrowBack,
} from "@mui/icons-material";

const validationSchema = Yup.object({
  firstName: Yup.string()
    .min(2, "First name must be at least 2 characters")
    .required("First name is required"),
  lastName: Yup.string()
    .min(2, "Last name must be at least 2 characters")
    .required("Last name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  phone: Yup.string()
    .matches(/^[0-9]{10}$/, "Phone number must be 10 digits")
    .required("Phone number is required"),
  department: Yup.string().required("Department is required"),
  position: Yup.string().required("Position is required"),
  salary: Yup.number()
    .positive("Salary must be positive")
    .required("Salary is required"),
  dateOfJoining: Yup.date()
    .max(new Date(), "Date of joining cannot be in the future")
    .required("Date of joining is required"),
  employeeType: Yup.string()
    .oneOf(["full-time", "part-time", "contract"], "Invalid employee type")
    .required("Employee type is required"),
  dateOfLeaving: Yup.date()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .when("dateOfJoining", (dateOfJoining, schema) =>
      dateOfJoining
        ? schema.min(
            dateOfJoining,
            "Date of leaving cannot be before date of joining"
          )
        : schema
    ),
});

export default function EmployeeAdd() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      department: "",
      position: "",
      salary: "",
      dateOfJoining: "",
      employeeType: "full-time",
      dateOfLeaving: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const fullName = `${values.firstName} ${values.lastName}`
          .replace(/\s+/g, " ")
          .trim();
        const payload = {
          name: fullName,
          email: values.email,
          password: "Password@123",
          role: "employee",
          phone: values.phone,
          department_id: values.department || null,
          position: values.position,
          designation: values.position,
          salary: Number(values.salary),
          joining_date: values.dateOfJoining,
          employee_type: values.employeeType,
        };

        if (values.dateOfLeaving) {
          payload.leaving_date = values.dateOfLeaving;
          payload.status = "inactive";
        }

        await api.post("/employees", payload);
        const defaultPassword = "Password@123";
        toast.success("Employee added successfully!");
        toast.info(
          `Default password: ${defaultPassword} — Share with the user and ask them to change it on first login.`,
          { autoClose: 10000 }
        );
        setTimeout(() => {
          navigate("/employees");
        }, 2000);
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to add employee");
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await api.get("/departments");
        const deptData = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        setDepartments(deptData);
      } catch (err) {
        toast.error("Failed to load departments");
        console.error("Failed to fetch departments:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDepartments();
  }, []);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <PageSkeleton type="form" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="secondary"
              onClick={() => navigate("/employees")}
              className="flex items-center gap-2"
            >
              <ArrowBack className="w-5 h-5" />
              Back
            </Button>
          </div>
          <h1 className="text-4xl font-bold text-primary mb-2">
            Add New Employee
          </h1>
          <p className="text-gray-600">Create a new employee record</p>
        </div>

        {/* Form */}
        <form onSubmit={formik.handleSubmit}>
          {/* Personal Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card mb-6"
          >
            <h3 className="text-xl font-bold text-primary mb-6 pb-4 border-b border-gray-200">
              Personal Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Person className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    First Name
                  </label>
                </div>
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.firstName && Boolean(formik.errors.firstName)
                      ? formik.errors.firstName
                      : ""
                  }
                  placeholder="Enter first name"
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Person className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Last Name
                  </label>
                </div>
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.lastName && Boolean(formik.errors.lastName)
                      ? formik.errors.lastName
                      : ""
                  }
                  placeholder="Enter last name"
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Email className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Email Address
                  </label>
                </div>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.email && Boolean(formik.errors.email)
                      ? formik.errors.email
                      : ""
                  }
                  placeholder="Enter email address"
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Phone Number
                  </label>
                </div>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.phone && Boolean(formik.errors.phone)
                      ? formik.errors.phone
                      : ""
                  }
                  placeholder="Enter phone number"
                />
              </div>
            </div>
          </motion.div>

          {/* Employment Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card mb-6"
          >
            <h3 className="text-xl font-bold text-primary mb-6 pb-4 border-b border-gray-200">
              Employment Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Business className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Department
                  </label>
                </div>
                <select
                  id="department"
                  name="department"
                  value={formik.values.department}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`input-field w-full ${
                    formik.touched.department && formik.errors.department
                      ? "border-red-500"
                      : ""
                  }`}
                >
                  <option value="">Select Department</option>
                  {departments.map((dept) => (
                    <option
                      key={dept._id || dept.id}
                      value={dept._id || dept.id}
                    >
                      {dept.name}
                    </option>
                  ))}
                </select>
                {formik.touched.department && formik.errors.department && (
                  <p className="text-sm text-red-600 mt-1">
                    {formik.errors.department}
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Work className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Position
                  </label>
                </div>
                <Input
                  id="position"
                  name="position"
                  type="text"
                  value={formik.values.position}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.position && Boolean(formik.errors.position)
                      ? formik.errors.position
                      : ""
                  }
                  placeholder="Enter position"
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <AttachMoney className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Salary
                  </label>
                </div>
                <Input
                  id="salary"
                  name="salary"
                  type="number"
                  value={formik.values.salary}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.salary && Boolean(formik.errors.salary)
                      ? formik.errors.salary
                      : ""
                  }
                  placeholder="Enter salary"
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Work className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Employee Type
                  </label>
                </div>
                <select
                  id="employeeType"
                  name="employeeType"
                  value={formik.values.employeeType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="input-field w-full"
                >
                  <option value="full-time">Full Time</option>
                  <option value="part-time">Part Time</option>
                  <option value="contract">Contract</option>
                </select>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CalendarToday className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Date of Joining
                  </label>
                </div>
                <Input
                  id="dateOfJoining"
                  name="dateOfJoining"
                  type="date"
                  value={formik.values.dateOfJoining}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.dateOfJoining &&
                    Boolean(formik.errors.dateOfJoining)
                      ? formik.errors.dateOfJoining
                      : ""
                  }
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CalendarToday className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Date of Leaving (Optional)
                  </label>
                </div>
                <Input
                  id="dateOfLeaving"
                  name="dateOfLeaving"
                  type="date"
                  value={formik.values.dateOfLeaving}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.dateOfLeaving &&
                    Boolean(formik.errors.dateOfLeaving)
                      ? formik.errors.dateOfLeaving
                      : ""
                  }
                />
              </div>
            </div>
          </motion.div>

          {/* Submit Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex justify-end gap-4"
          >
            <Button
              variant="secondary"
              onClick={() => navigate("/employees")}
              className="min-w-[120px]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={formik.isSubmitting}
              className="min-w-[200px]"
            >
              {formik.isSubmitting ? "Adding..." : "Add Employee"}
            </Button>
          </motion.div>
        </form>
      </motion.div>
    </div>
  );
}
